import Observability from '@launchdarkly/observability';
import SessionReplay from '@launchdarkly/session-replay';
import { basicLogger, useLDClient, withLDProvider } from 'launchdarkly-react-client-sdk';
import {
  createContext,
  type FC,
  type PropsWithChildren,
  useCallback,
  useContext,
} from 'react';
import { config } from '../../../modules/config';

interface MonitoringContextValue {
  identifyUser: (key: string, attributes?: Record<string, string>) => void;
}

const MonitoringContext = createContext<MonitoringContextValue>({
  identifyUser: () => {},
});

export const useMonitoring = () => useContext(MonitoringContext);

const MonitoringInner: FC<PropsWithChildren> = ({ children }) => {
  const ldClient = useLDClient();

  const identifyUser = useCallback(
    (key: string, attributes?: Record<string, string>) => {
      ldClient?.identify({ kind: 'user', key, ...attributes });
    },
    [ldClient],
  );

  return (
    <MonitoringContext.Provider value={{ identifyUser }}>
      {children}
    </MonitoringContext.Provider>
  );
};

const LDWrapped = config.launchDarkly.clientSideId
  ? withLDProvider({
      clientSideID: config.launchDarkly.clientSideId,
      context: { kind: 'user', key: 'anonymous' },
      options: {
        logger: basicLogger({ level: 'info' }),
        plugins: [
          new Observability({
            tracingOrigins: true,
            networkRecording: {
              enabled: true,
              recordHeadersAndBody: true,
            },
            serviceName: config.launchDarkly.serviceName,
            environment: config.launchDarkly.environment,
          }),
          new SessionReplay({
            privacySetting: 'strict',
            serviceName: config.launchDarkly.serviceName,
            environment: config.launchDarkly.environment,
          }),
        ],
      },
    })(MonitoringInner)
  : MonitoringInner;

export const MonitoringProvider: FC<PropsWithChildren> = ({ children }) => (
  <LDWrapped>{children}</LDWrapped>
);
